import { useRef, useState } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { ChartTheme, PieChartDatum } from '../types';
import { DASHBOARD_LABELS as LABELS } from '../../../utils/labels';

interface MachinePieChartProps {
  data: PieChartDatum[];
  ariaLabel: string;
  chartTheme: ChartTheme;
  onSelect: (key: string) => void;
  testId: string;
}

export default function MachinePieChart({ data, ariaLabel, chartTheme, onSelect, testId }: MachinePieChartProps) {
  const [activeIdx, setActiveIdx] = useState<number | undefined>();
  const kbActive = useRef(false);
  const { chartTextColor, tooltipStyle } = chartTheme;

  return (
    <div
      tabIndex={0}
      role="group"
      aria-label={ariaLabel}
      data-testid={testId}
      className="outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
      onFocus={(e) => {
        if (e.target === e.currentTarget) {
          kbActive.current = true;
          setActiveIdx(0);
        }
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          kbActive.current = false;
          setActiveIdx(undefined);
        }
      }}
      onKeyDown={(e) => {
        const len = data.length;
        if (!len) return;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          setActiveIdx((prev) => ((prev ?? -1) + 1) % len);
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          setActiveIdx((prev) => ((prev ?? 0) - 1 + len) % len);
        } else if ((e.key === 'Enter' || e.key === ' ') && activeIdx !== undefined) {
          e.preventDefault();
          onSelect(data[activeIdx].key);
        }
      }}
    >
      <ResponsiveContainer width="100%" height={260}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={68}
            outerRadius={100}
            paddingAngle={3}
            dataKey="value"
            cursor="pointer"
            activeIndex={activeIdx}
            onMouseEnter={(_, idx) => {
              if (!kbActive.current) setActiveIdx(idx);
            }}
            onMouseLeave={() => {
              if (!kbActive.current) setActiveIdx(undefined);
            }}
            onClick={(entry: { key: string }) => onSelect(entry.key)}
          >
            {data.map((entry, idx) => (
              <Cell key={idx} fill={entry.color} />
            ))}
          </Pie>
          <ChartTooltip
            contentStyle={tooltipStyle}
            separator=""
            formatter={(value: number) => [`${value} ${LABELS.machines.toLowerCase()}`, '']}
          />
          <Legend
            content={() => (
              <div className="flex justify-center flex-wrap gap-x-4 gap-y-1 pt-1">
                {data.map(({ name, color }) => (
                  <span
                    key={name}
                    className="flex items-center gap-1.5"
                    style={{ color: chartTextColor, fontSize: '0.8125rem' }}
                  >
                    <span
                      className="inline-block w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: color }}
                    />
                    {name}
                  </span>
                ))}
              </div>
            )}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}
